import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import {
  getModifierGroups,
  createModifierGroup,
  updateModifierGroup,
  deleteModifierGroup,
} from '../services/api';
import MenuNav from '../components/MenuNav';
import HelpIcon from '../components/help/HelpIcon';
import { formatCurrency } from '../utils/formatCurrency';
import { storage } from '../utils/storage';
import './ModifiersPage.css';

function ModifiersPage() {
  const { t } = useTranslation();
  const [modifierGroups, setModifierGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restaurantId, setRestaurantId] = useState(storage.get('selectedRestaurantId') || '');
  const [isEditing, setIsEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [highlightedGroupId, setHighlightedGroupId] = useState(null);
  const groupRefs = useRef({});

  // Form State
  const [currentGroup, setCurrentGroup] = useState({
    name: '',
    minSelect: 0,
    maxSelect: 1,
    required: false,
    options: [],
  });

  // Compute duplicate name indices for visual disambiguation
  const duplicateNameMap = useMemo(() => {
    const nameCount = {};
    const nameIndex = {};
    modifierGroups.forEach((g) => {
      nameCount[g.name] = (nameCount[g.name] || 0) + 1;
    });
    const result = {};
    modifierGroups.forEach((g) => {
      if (nameCount[g.name] > 1) {
        nameIndex[g.name] = (nameIndex[g.name] || 0) + 1;
        result[g.groupId] = { index: nameIndex[g.name], total: nameCount[g.name] };
      }
    });
    return result;
  }, [modifierGroups]);

  useEffect(() => {
    const handleRestaurantChange = () => {
      const id = storage.get('selectedRestaurantId');
      setRestaurantId(id);
    };
    window.addEventListener('restaurantChanged', handleRestaurantChange);
    window.addEventListener('storage', handleRestaurantChange);
    return () => {
      window.removeEventListener('restaurantChanged', handleRestaurantChange);
      window.removeEventListener('storage', handleRestaurantChange);
    };
  }, []);

  useEffect(() => {
    if (restaurantId) {
      loadModifierGroups();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const loadModifierGroups = async () => {
    try {
      setLoading(true);
      const data = await getModifierGroups(restaurantId);
      setModifierGroups(data.modifierGroups || []);
    } catch (error) {
      console.error('Error loading modifier groups:', error);
      alert('Error loading modifier groups');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (group) => {
    setCurrentGroup(group);
    setIsEditing(true);
    setShowForm(true);
  };

  const confirmDelete = (group) => {
    setGroupToDelete(group);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!groupToDelete) return;
    try {
      await deleteModifierGroup(restaurantId, groupToDelete.groupId);
      setShowDeleteConfirm(false);
      setGroupToDelete(null);
      loadModifierGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
      alert('Error deleting group');
    }
  };

  const handleCreateNew = () => {
    setCurrentGroup({
      name: '',
      minSelect: 0,
      maxSelect: 1,
      required: false,
      options: [{ name: '', price: 0 }],
    });
    setIsEditing(false);
    setShowForm(true);
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...currentGroup.options];
    newOptions[index][field] = value;
    setCurrentGroup({ ...currentGroup, options: newOptions });
  };

  const addOption = () => {
    setCurrentGroup({
      ...currentGroup,
      options: [...currentGroup.options, { name: '', price: 0 }],
    });
  };

  const removeOption = (index) => {
    const newOptions = currentGroup.options.filter((_, i) => i !== index);
    setCurrentGroup({ ...currentGroup, options: newOptions });
  };

  const isDuplicateName = (name) => {
    const trimmed = name.trim().toLowerCase();
    return modifierGroups.some(
      (g) =>
        g.name.trim().toLowerCase() === trimmed &&
        (!isEditing || g.groupId !== currentGroup.groupId)
    );
  };

  const scrollToGroup = useCallback((groupId) => {
    setHighlightedGroupId(groupId);
    requestAnimationFrame(() => {
      const el = groupRefs.current[groupId];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
    setTimeout(() => setHighlightedGroupId(null), 2000);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDuplicateName(currentGroup.name)) {
      alert(
        t('modifiers.duplicateNameError', 'Ya existe un grupo con ese nombre. Usá un nombre único.')
      );
      return;
    }
    try {
      let newGroupId = null;
      if (isEditing) {
        await updateModifierGroup(restaurantId, currentGroup.groupId, currentGroup);
        newGroupId = currentGroup.groupId;
      } else {
        const created = await createModifierGroup(restaurantId, currentGroup);
        newGroupId = created?.groupId || created?.id;
      }
      setShowForm(false);
      const data = await getModifierGroups(restaurantId);
      const groups = data.modifierGroups || [];
      setModifierGroups(groups);
      if (newGroupId) {
        requestAnimationFrame(() => scrollToGroup(newGroupId));
      }
    } catch (error) {
      console.error('Error saving modifier group:', error);
      alert('Error saving modifier group');
    }
  };

  if (!restaurantId) return <div className="p-4">{t('common.selectRestaurant')}</div>;
  if (loading && !showForm) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="modifiers-page">
      <MenuNav />
      <div className="page-header mb-4">
        <h1>
          {t('modifiers.title', 'Modificadores y Extras')}{' '}
          <HelpIcon topicKey="modifiers.overview" />
        </h1>
        {!showForm && (
          <button type="button" className="btn btn-primary" onClick={handleCreateNew}>
            {t('modifiers.newGroup', 'Nuevo Grupo')}
          </button>
        )}
      </div>

      {showForm ? (
        <div className="card p-4">
          <h2>{isEditing ? t('modifiers.editGroup') : t('modifiers.createGroup')}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group mb-3">
              <label htmlFor="modifier-group-name">{t('common.name')}</label>
              <input
                id="modifier-group-name"
                type="text"
                className="form-control"
                value={currentGroup.name}
                onChange={(e) => setCurrentGroup({ ...currentGroup, name: e.target.value })}
                required
              />
            </div>
            <div className="form-row mb-3" style={{ display: 'flex', gap: '20px' }}>
              <div className="form-group">
                <label htmlFor="modifier-min-select">
                  {t('modifiers.minSelect', 'Mínimo')}{' '}
                  <HelpIcon topicKey="modifiers.minMaxSelect" />
                </label>
                <input
                  id="modifier-min-select"
                  type="number"
                  className="form-control"
                  value={currentGroup.minSelect}
                  onChange={(e) =>
                    setCurrentGroup({ ...currentGroup, minSelect: parseInt(e.target.value) })
                  }
                  min="0"
                />
              </div>
              <div className="form-group">
                <label htmlFor="modifier-max-select">{t('modifiers.maxSelect', 'Máximo')}</label>
                <input
                  id="modifier-max-select"
                  type="number"
                  className="form-control"
                  value={currentGroup.maxSelect}
                  onChange={(e) =>
                    setCurrentGroup({ ...currentGroup, maxSelect: parseInt(e.target.value) })
                  }
                  min="1"
                />
              </div>
              <div className="form-group" style={{ alignSelf: 'flex-end' }}>
                <label htmlFor="modifier-required">
                  <input
                    id="modifier-required"
                    type="checkbox"
                    checked={currentGroup.required}
                    onChange={(e) =>
                      setCurrentGroup({ ...currentGroup, required: e.target.checked })
                    }
                  />{' '}
                  {t('common.required', 'Obligatorio')}
                </label>{' '}
                <HelpIcon topicKey="modifiers.required" />
              </div>
            </div>

            <h2>{t('common.options', 'Opciones')}</h2>
            <div className="options-list mb-3">
              <div className="option-row-header">
                <span className="option-header-name">{t('modifiers.columnName', 'Nombre')}</span>
                <span className="option-header-price">
                  {t('modifiers.columnPrice', 'Precio extra')}
                </span>
                <span className="option-header-action" aria-hidden="true"></span>
              </div>
              {currentGroup.options.map((option, index) => (
                <div key={index} className="option-row mb-2">
                  <input
                    type="text"
                    placeholder={t('modifiers.optionNamePlaceholder', 'Nombre opción')}
                    aria-label={t('modifiers.optionNameLabel', {
                      number: index + 1,
                      defaultValue: `Nombre opción ${index + 1}`,
                    })}
                    value={option.name}
                    onChange={(e) => handleOptionChange(index, 'name', e.target.value)}
                    required
                  />
                  <input
                    type="number"
                    placeholder={t('modifiers.optionPricePlaceholder', 'Precio Extra')}
                    aria-label={t('modifiers.optionPriceLabel', {
                      number: index + 1,
                      defaultValue: `Precio extra opción ${index + 1}`,
                    })}
                    value={option.price}
                    onChange={(e) => handleOptionChange(index, 'price', e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn-danger btn-sm"
                    onClick={() => removeOption(index)}
                  >
                    X
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-secondary btn-sm" onClick={addOption}>
                + {t('common.addOption', 'Agregar Opción')}
              </button>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary">
                {t('common.save')}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="grid-container">
          {modifierGroups.map((group) => (
            <div
              key={group.groupId}
              ref={(el) => {
                groupRefs.current[group.groupId] = el;
              }}
              className={`card p-3 modifier-card${highlightedGroupId === group.groupId ? ' modifier-card--highlight' : ''}`}
            >
              <div className="card-header-flex">
                <h2>
                  {group.name}
                  {duplicateNameMap[group.groupId] && (
                    <span className="duplicate-index">
                      {' '}
                      ({duplicateNameMap[group.groupId].index})
                    </span>
                  )}
                </h2>
                <div className="modifier-actions">
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => handleEdit(group)}
                    title={t('common.edit')}
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    type="button"
                    className="btn-icon btn-icon--danger"
                    onClick={() => confirmDelete(group)}
                    title={t('common.delete')}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <p className="text-muted">
                {group.required
                  ? t('common.required', 'Obligatorio')
                  : t('common.optional', 'Opcional')}{' '}
                • Min: {group.minSelect} / Max: {group.maxSelect} •{' '}
                {t('modifiers.optionsCount', { count: group.options.length })}
              </p>
              <ul className="options-preview">
                {group.options.slice(0, 3).map((opt, i) => (
                  <li key={i}>
                    {opt.name} {opt.price > 0 && `(+${formatCurrency(opt.price)})`}
                  </li>
                ))}
                {group.options.length > 3 && <li>...</li>}
              </ul>
            </div>
          ))}
        </div>
      )}
      {showDeleteConfirm && groupToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('modifiers.deleteGroup')}</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowDeleteConfirm(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>{t('modifiers.deleteGroupConfirmation', { name: groupToDelete.name })}</p>
              <p className="warning-text">{t('modifiers.deleteGroupWarning')}</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowDeleteConfirm(false)}
              >
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDelete}>
                {t('menu.confirmDelete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ModifiersPage;
